import { useCallback, useEffect, useMemo, useState } from 'react';
import { downloadFile } from '@/utils/dataDownloadUtils';
import { useFileFetcher } from '@/services/queryUtils';
import type { DownloadSettingsState, DownloadDrawerProps } from './downloadDrawer.d';
import { EARNINGS_DOWNLOAD_DEFAULT_SHOW_IDS } from './downloadDrawerUtils';
import DownloadDrawerFiltersEcho from './DownloadDrawerFiltersEcho';
import BreakdownBySectionEcho from './ReportsToDownloadSectionEcho';
import type { DateRange, DataTypes } from '@/types';
import { mixpanelTrack, RevenueDashboardWidget, MIXPANEL_EVENT } from '@/utils';
import LevelOfDetailSectionEcho from './LevelOfDetailSectionEcho';
import { LevelOfDetail } from '@/types';
import { Button, Drawer, message, Typography } from 'antd';
import { SyncOutlined } from '@ant-design/icons';

/**
 * DownloadDrawer component for downloading earnings data as CSV files
 * @param {boolean} isOpen - Whether the drawer is open
 * @param {() => void} handleClose - Function to close the drawer
 * @param {Network | undefined} selectedNetwork - The selected network
 * @param {GlobalFilter} globalFilters - The global filters
 * @param {Show[]} shows - The shows to download
 */
const DownloadDrawer = ({
  isOpen,
  handleClose,
  selectedNetwork,
  globalFilters,
  shows,
}: DownloadDrawerProps) => {
  const fetchFile = useFileFetcher();

  const defaultDownloadSettings: DownloadSettingsState = {
    shows: EARNINGS_DOWNLOAD_DEFAULT_SHOW_IDS,
    dateRange: globalFilters.dateRange,
    include: {
      shows: true,
      campaign_types: true,
      advertisers: true,
      campaigns: true,
    },
  };

  const [selectedLevelOfDetails, setSelectedLevelOfDetails] = useState<LevelOfDetail>(
    LevelOfDetail.total
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSettings, setDownloadSettings] =
    useState<DownloadSettingsState>(defaultDownloadSettings);

  const numFilesToDownload = Object.values(downloadSettings.include).filter(Boolean).length;
  const downloadButtonText = `Download (${numFilesToDownload} CSV${
    numFilesToDownload === 1 ? '' : 's'
  })`;

  const showOptions = useMemo(
    () => shows.map((show) => ({ value: show.id, label: show.name })),
    [shows]
  );

  const onDateOpen = useCallback(() => {
    mixpanelTrack(MIXPANEL_EVENT.earnings.downloadDrawer.dateOpened, {
      widget: RevenueDashboardWidget.DownloadDrawer,
      page: 'earnings',
    });
  }, []);

  const onDatePresetSelect = useCallback((preset: string) => {
    mixpanelTrack(MIXPANEL_EVENT.earnings.downloadDrawer.datePresetSelected, {
      widget: RevenueDashboardWidget.DownloadDrawer,
      page: 'earnings',
      preset,
    });
  }, []);

  const onDateApply = useCallback((from: string, to: string) => {
    mixpanelTrack(MIXPANEL_EVENT.earnings.downloadDrawer.dateApplied, {
      widget: RevenueDashboardWidget.DownloadDrawer,
      page: 'earnings',
      start: from,
      end: to,
    });
  }, []);

  const setDateRange = useCallback(({ from, to }: DateRange) => {
    setDownloadSettings((prev) => ({
      ...prev,
      dateRange: { from, to },
    }));
  }, []);

  useEffect(() => {
    if (isOpen) setDownloadSettings(defaultDownloadSettings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleDownload = async () => {
    const mixPanelProps = {
      breakdown: selectedLevelOfDetails,
      networkName: selectedNetwork?.name ?? '',
      shows: downloadSettings.shows.length > 0 ? downloadSettings.shows : 'all',
      start: downloadSettings.dateRange.from,
      end: downloadSettings.dateRange.to,
      includesShows: downloadSettings.include.shows,
      includesCampaignTypes: downloadSettings.include.campaign_types,
      includesAdvertisers: downloadSettings.include.advertisers,
      includesCampaigns: downloadSettings.include.campaigns,
    };
    setIsDownloading(true);

    try {
      const networkId = selectedNetwork?.id ?? '';
      const { from, to } = downloadSettings.dateRange;
      const showIds = downloadSettings.shows.length > 0 ? downloadSettings.shows : undefined;

      const dataTypesToFetch: DataTypes[] = (
        Object.entries(downloadSettings.include) as [DataTypes, boolean][]
      )
        .filter(([, enabled]) => enabled)
        .map(([type]) => type);

      if (dataTypesToFetch.length === 0) {
        message.error('No data to download');
        return;
      }

      const endpointMap: Record<DataTypes, string> = {
        shows: 'shows',
        campaigns: 'campaigns',
        advertisers: 'advertisers',
        campaign_types: 'campaign-types',
      };
      const filesParam = dataTypesToFetch.map((type) => endpointMap[type]).join(',');

      const searchParams = new URLSearchParams();
      searchParams.append('from', from);
      searchParams.append('to', to);
      searchParams.append('files', filesParam);
      if (showIds?.length) {
        searchParams.append('showIds', showIds.join(','));
      }
      const path = `/v2/networks/${networkId}/ongoing/export/${selectedLevelOfDetails}?${searchParams.toString()}`;

      const { blob, fileName } = await fetchFile(path);
      downloadFile(blob, fileName);

      mixpanelTrack(MIXPANEL_EVENT.earnings.downloadDrawer.downloadSuccess, {
        ...mixPanelProps,
        widget: RevenueDashboardWidget.DownloadDrawer,
        page: 'earnings',
      });
    } catch (error) {
      mixpanelTrack(MIXPANEL_EVENT.earnings.downloadDrawer.downloadFailed, {
        ...mixPanelProps,
        errorCode: 'UNKNOWN_ERROR',
        widget: RevenueDashboardWidget.DownloadDrawer,
        page: 'earnings',
      });
      console.error('Error downloading data', error);
      message.error('Failed to download data');
    } finally {
      setIsDownloading(false);
    }
  };

  const drawerFooter = (
    <div className="flex w-full flex-row justify-end gap-3">
      <Button danger type="text" onClick={handleClose}>
        Cancel
      </Button>
      <Button
        variant="outlined"
        color="primary"
        disabled={numFilesToDownload === 0}
        onClick={handleDownload}
        loading={isDownloading ? { icon: <SyncOutlined spin /> } : false}
      >
        {downloadButtonText}
      </Button>
    </div>
  );

  return (
    <Drawer
      open={isOpen}
      data-testid="download-drawer"
      footer={drawerFooter}
      size="large"
      title={
        <Typography.Title level={3} style={{ margin: 0 }}>
          Download earnings data (CSV)
        </Typography.Title>
      }
      onClose={handleClose}
    >
      <div className="h-full flex flex-col">
        <DownloadDrawerFiltersEcho
          showOptions={showOptions}
          downloadSettings={downloadSettings}
          setDownloadSettings={setDownloadSettings}
          setDateRange={setDateRange}
          onDateOpen={onDateOpen}
          onDatePresetSelect={onDatePresetSelect}
          onDateApply={onDateApply}
        />
        <LevelOfDetailSectionEcho
          selectedLevelOfDetails={selectedLevelOfDetails}
          setSelectedLevelOfDetails={setSelectedLevelOfDetails}
        />
        <BreakdownBySectionEcho
          downloadSettings={downloadSettings}
          setDownloadSettings={setDownloadSettings}
        />
      </div>
    </Drawer>
  );
};

export default DownloadDrawer;
